import { type ChangeEvent, type FormEvent } from "react"
import { useForm, usePage } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LocationSectionScreen } from "@/features/staff/location-section-screen"
import { t } from "@/lib/i18n"

const tSection = (key: string) =>
  t(`ui.react.pages.staff.location.manage.location.sections.${key}`)

type TaxRate = {
  name: string
  /** A decimal percentage as a string ("16.0"), never a float — see TaxSettings. */
  rate: string
}

/**
 * Rates travel as strings both ways. The whole tax stack is exact arithmetic
 * (`Location::TaxPolicy` stores BigDecimal-formatted strings on purpose), and a
 * round trip through a JS number is exactly how 16 becomes 15.999999999999998
 * and moves a cent on somebody's bill.
 *
 * `regimeOptions` and `maxRates` come from the server so this screen cannot
 * offer a regime or a row count `Location::TaxPolicy` would then drop.
 */
type TaxSettings = {
  regime: string
  regimeOptions: { value: string; label: string }[]
  pricesIncludeTax: boolean
  rates: TaxRate[]
  maxRates: number
}

type Props = { taxes: TaxSettings }

function TaxesCard({ taxes }: { taxes: TaxSettings }) {
  // A fixed row of `maxRates` fields, like the tip suggestions above: an empty
  // row is how a rate is removed, so the rows cannot be driven by how many
  // rates happen to be saved.
  const padded: TaxRate[] = Array.from(
    { length: taxes.maxRates },
    (_, index) => taxes.rates[index] ?? { name: "", rate: "" }
  )

  const { data, setData, patch, processing } = useForm({
    taxes: {
      regime: taxes.regime,
      rates: padded,
    },
  })

  const rows = data.taxes.rates
  const noneConfigured = rows.every((row) => row.name.trim() === "" && row.rate.trim() === "")

  // Digits and one decimal separator. Four decimals is the policy's cap and the
  // server rounds to it, so nothing here needs to know that number.
  const sanitizePercentage = (value: string) => {
    const [whole, ...rest] = value.replace(/[^\d.]/g, "").split(".")
    return rest.length > 0 ? `${whole}.${rest.join("")}` : whole
  }

  const setRate = (index: number, field: keyof TaxRate, value: string) => {
    setData("taxes", {
      ...data.taxes,
      rates: rows.map((row, position) => (position === index ? { ...row, [field]: value } : row)),
    })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    patch("/manage/location/taxes", { preserveScroll: true })
  }

  return (
    <Card data-testid="location-taxes-card">
      <CardContent>
        <form onSubmit={submit} className="space-y-6" data-testid="location-taxes-form">
          {/* The regime is stated, not picked.
              `regimeOptions` currently carries exactly one entry: nothing adds a
              forward regime's tax to what a diner is charged, so the server does
              not offer one (see Staff::Location::Manage::LocationProps#tax_props).
              A select with a single option is a control that does nothing, which
              docs/product.md's honest-UI rule forbids. When the charge rails learn
              to carry forward tax the server will offer a second option and this
              becomes a Select again. */}
          <section className="space-y-2">
            <Label>{tSection("taxes_regime_label")}</Label>
            <p className="text-sm font-medium" data-testid="tax-regime">
              {taxes.regimeOptions.find((option) => option.value === taxes.regime)?.label ?? taxes.regime}
            </p>

            {/* The one thing a manager has to understand before typing a number:
                whether 16 comes out of the menu price or gets added to it. It sits
                above the fields and is always visible — a tooltip would hide the
                only sentence that changes what the number means. */}
            <div
              className="rounded-lg border border-border bg-muted/50 p-3 text-sm"
              data-testid="tax-prices-include-notice"
            >
              {tSection("taxes_prices_included")}
            </div>
          </section>

          <section className="space-y-2">
            <Label>{tSection("taxes_rates_label")}</Label>
            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2 text-xs text-muted-foreground" aria-hidden="true">
              <span>{tSection("taxes_rate_name")}</span>
              <span>{tSection("taxes_rate_percentage")}</span>
            </div>
            <div className="space-y-2">
              {rows.map((row, index) => {
                // Policy rule 2: a rate with no name is dropped, because the name
                // is what the diner reads on the receipt. Without this the manager
                // types 16, tabs away, saves, and the rate silently vanishes.
                const missingName = row.name.trim() === "" && row.rate.trim() !== ""

                return (
                  <div key={index} className="space-y-1">
                    <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
                      <Input
                        id={`tax-rate-name-${index}`}
                        value={row.name}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setRate(index, "name", event.target.value)}
                        placeholder={tSection("taxes_rate_name_placeholder")}
                        aria-label={`${tSection("taxes_rate_name")} ${index + 1}`}
                        aria-invalid={missingName || undefined}
                        data-testid={`tax-rate-name-${index}`}
                      />
                      <Input
                        id={`tax-rate-percentage-${index}`}
                        inputMode="decimal"
                        value={row.rate}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setRate(index, "rate", sanitizePercentage(event.target.value))
                        }
                        placeholder={tSection("taxes_rate_percentage_placeholder")}
                        className="tabular-nums"
                        aria-label={`${tSection("taxes_rate_percentage")} ${index + 1}`}
                        data-testid={`tax-rate-percentage-${index}`}
                      />
                    </div>
                    {missingName && (
                      <p className="text-xs text-warning" data-testid={`tax-rate-missing-name-${index}`}>
                        {tSection("taxes_rate_missing_name")}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
            <p className="text-xs text-muted-foreground">{tSection("taxes_rates_hint")}</p>
            {noneConfigured && (
              <p className="text-xs text-muted-foreground" data-testid="tax-rates-empty">
                {tSection("taxes_empty_note")}
              </p>
            )}
          </section>

          <Button type="submit" disabled={processing} data-testid="tax-rates-submit">
            {tSection("taxes_submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}


export default function StaffLocationManageLocationTaxes() {
  const { taxes } = usePage<Props>().props

  return (
    <LocationSectionScreen title={tSection("taxes_title")} description={tSection("taxes_description")}>
      <TaxesCard taxes={taxes} />
    </LocationSectionScreen>
  )
}
